import { useState, useEffect } from "react";
import { useApi } from "@/lib/api";
import { toast } from "sonner";
import { 
  Building2, 
  Plus, 
  ExternalLink, 
  Edit2, 
  Trash2, 
  Globe,
  Target,
  TrendingUp,
  Award,
  MapPin,
  Search,
  Filter
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

// Initial competitor data
const INITIAL_COMPETITORS = [
  {
    id: "take-e-way",
    name: "Take-e-way",
    website: "https://www.take-e-way.de",
    description: "EPR compliance services for packaging, WEEE, and batteries across Europe",
    headquarters: "Germany",
    markets: ["DE", "FR", "AT", "NL", "ES", "IT", "PL"],
    services: ["Packaging EPR", "WEEE", "Batteries", "Authorised Representative"],
    strengths: ["Strong German market presence", "Multi-country coverage", "Established brand"],
    weaknesses: ["Complex pricing", "Limited digital tools"],
    threat_level: "high",
    notes: ""
  },
  {
    id: "recycleme",
    name: "RecycleMe",
    website: "https://www.recycleme.eu",
    description: "Seamless EPR compliance across EU with Authorised Representatives",
    headquarters: "Netherlands",
    markets: ["NL", "DE", "FR", "BE", "AT"],
    services: ["Packaging EPR", "Authorised Representative", "Consulting"],
    strengths: ["User-friendly platform", "Competitive pricing"],
    weaknesses: ["Smaller market coverage", "Less brand recognition"],
    threat_level: "medium",
    notes: ""
  },
  {
    id: "pincvision",
    name: "Pincvision",
    website: "https://www.pincvision.com",
    description: "Global compliance solutions for packaging and product stewardship",
    headquarters: "Netherlands",
    markets: ["NL", "DE", "FR", "UK", "BE", "AT", "ES", "IT"],
    services: ["Packaging EPR", "WEEE", "Batteries", "Consulting", "Software"],
    strengths: ["Comprehensive software platform", "Global reach", "Strong partnerships"],
    weaknesses: ["Higher pricing tier", "Enterprise focus"],
    threat_level: "high",
    notes: ""
  },
  {
    id: "drs",
    name: "Deutsche Recycling Service GmbH",
    website: "https://www.drs-gmbh.de",
    description: "German environmental compliance and recycling services",
    headquarters: "Germany",
    markets: ["DE"],
    services: ["Packaging EPR", "WEEE", "Batteries", "Hazardous Waste"],
    strengths: ["Deep German expertise", "Local relationships"],
    weaknesses: ["Germany-only focus", "Limited digital presence"],
    threat_level: "low",
    notes: ""
  },
  {
    id: "sphera",
    name: "Sphera",
    website: "https://sphera.com",
    description: "Sustainability Software & Operational Risk Management",
    headquarters: "USA",
    markets: ["Global"],
    services: ["Sustainability Software", "ESG Reporting", "Compliance Management"],
    strengths: ["Enterprise software leader", "Comprehensive ESG platform", "Global presence"],
    weaknesses: ["Not EPR-focused", "High cost", "Complex implementation"],
    threat_level: "medium",
    notes: ""
  },
  {
    id: "oeko",
    name: "Öko-Institut",
    website: "https://www.oeko.de/en/",
    description: "Independent research and consultancy for sustainable development",
    headquarters: "Germany",
    markets: ["DE", "EU"],
    services: ["Research", "Consulting", "Policy Advisory"],
    strengths: ["Scientific credibility", "Policy influence"],
    weaknesses: ["Not a direct service provider", "Academic focus"],
    threat_level: "low",
    notes: ""
  },
  {
    id: "certify",
    name: "Certify GmbH",
    website: "https://www.certify-gmbh.de/en/",
    description: "Certification and compliance services for German market",
    headquarters: "Germany",
    markets: ["DE"],
    services: ["Certification", "Packaging EPR", "Consulting"],
    strengths: ["German market expertise", "Certification services"],
    weaknesses: ["Limited geographic scope"],
    threat_level: "low",
    notes: ""
  },
  {
    id: "zentek",
    name: "Zentek",
    website: "https://www.zentek.de/en/eprcompliance/",
    description: "EPR compliance and environmental services",
    headquarters: "Germany",
    markets: ["DE", "AT", "CH"],
    services: ["Packaging EPR", "WEEE", "Consulting"],
    strengths: ["Strong DACH presence", "Integrated services"],
    weaknesses: ["Limited EU coverage"],
    threat_level: "medium",
    notes: ""
  },
  {
    id: "my-compliance",
    name: "My-Compliance",
    website: "https://www.my-compliance.eu/",
    description: "EU-wide EPR compliance platform",
    headquarters: "EU",
    markets: ["EU-wide"],
    services: ["Packaging EPR", "Digital Platform", "Authorised Representative"],
    strengths: ["Modern platform", "EU-wide focus"],
    weaknesses: ["Newer entrant", "Building market presence"],
    threat_level: "medium",
    notes: ""
  }
];

const THREAT_COLORS = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-blue-50 text-blue-700 border-blue-200"
};

const SERVICE_COLORS = {
  "Packaging EPR": "bg-blue-50 text-blue-700",
  "WEEE": "bg-purple-50 text-purple-700",
  "Batteries": "bg-amber-50 text-amber-700",
  "Authorised Representative": "bg-green-50 text-green-700",
  "Consulting": "bg-neutral-100 text-neutral-600",
  "Software": "bg-blue-50 text-blue-700"
};

export default function Competitors() {
  const api = useApi();
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [threatFilter, setThreatFilter] = useState("all");
  const [expandedCard, setExpandedCard] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    website: "",
    description: "",
    headquarters: "",
    markets: "",
    services: "",
    pricing: "",
    strengths: "",
    weaknesses: "",
    opportunities: "",
    threats: "",
    threat_level: "medium",
    notes: ""
  });

  useEffect(() => {
    fetchCompetitors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCompetitors = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/competitors/");
      if (response.data?.competitors?.length > 0) {
        setCompetitors(response.data.competitors);
      } else {
        // Use initial data if no saved competitors
        setCompetitors(INITIAL_COMPETITORS);
      }
    } catch (error) {
      console.error("Error fetching competitors:", error);
      // Use initial data on error
      setCompetitors(INITIAL_COMPETITORS);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    try {
      const payload = {
        ...formData,
        markets: formData.markets.split(",").map(m => m.trim()).filter(Boolean),
        services: formData.services.split(",").map(s => s.trim()).filter(Boolean),
        strengths: formData.strengths.split("\n").map(s => s.trim()).filter(Boolean),
        weaknesses: formData.weaknesses.split("\n").map(w => w.trim()).filter(Boolean),
        opportunities: formData.opportunities.split("\n").map(o => o.trim()).filter(Boolean),
        threats: formData.threats.split("\n").map(t => t.trim()).filter(Boolean)
      };

      if (editingCompetitor) {
        await api.put(`/api/competitors/${editingCompetitor.id}`, payload);
        toast.success("Competitor updated");
      } else {
        await api.post("/api/competitors/", payload);
        toast.success("Competitor added");
      }
      
      setShowDialog(false);
      setEditingCompetitor(null);
      resetForm();
      fetchCompetitors();
    } catch (error) {
      console.error("Error saving competitor:", error);
      toast.error("Failed to save competitor");
    }
  };

  const handleEdit = (competitor) => {
    setEditingCompetitor(competitor);
    setFormData({
      name: competitor.name,
      website: competitor.website || "",
      description: competitor.description || "",
      headquarters: competitor.headquarters || "",
      markets: competitor.markets?.join(", ") || "",
      services: competitor.services?.join(", ") || "",
      pricing: competitor.pricing || "",
      strengths: competitor.strengths?.join("\n") || "",
      weaknesses: competitor.weaknesses?.join("\n") || "",
      opportunities: competitor.opportunities?.join("\n") || "",
      threats: competitor.threats?.join("\n") || "",
      threat_level: competitor.threat_level || "medium",
      notes: competitor.notes || ""
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this competitor?")) return;
    
    try {
      await api.delete(`/api/competitors/${id}`);
      toast.success("Competitor deleted");
      fetchCompetitors();
    } catch (error) {
      toast.error("Failed to delete competitor");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      website: "",
      description: "",
      headquarters: "",
      markets: "",
      services: "",
      pricing: "",
      strengths: "",
      weaknesses: "",
      opportunities: "",
      threats: "",
      threat_level: "medium",
      notes: ""
    });
  };

  // Filter competitors
  const filteredCompetitors = competitors.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         c.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesThreat = threatFilter === "all" || c.threat_level === threatFilter;
    return matchesSearch && matchesThreat;
  });

  // Stats
  const stats = {
    total: competitors.length,
    high: competitors.filter(c => c.threat_level === "high").length,
    medium: competitors.filter(c => c.threat_level === "medium").length,
    low: competitors.filter(c => c.threat_level === "low").length
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">Competitor Analysis</h1>
          <p className="text-sm text-neutral-500 mt-1">Track and analyze your market competitors</p>
        </div>
        <Button onClick={() => { resetForm(); setEditingCompetitor(null); setShowDialog(true); }} data-testid="add-competitor-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Competitor
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-xl">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-neutral-900">{stats.total}</p>
                <p className="text-sm text-neutral-500">Total Competitors</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-50 rounded-xl">
                <Target className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-neutral-900">{stats.high}</p>
                <p className="text-sm text-neutral-500">High Threat</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-50 rounded-xl">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-neutral-900">{stats.medium}</p>
                <p className="text-sm text-neutral-500">Medium Threat</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-xl">
                <Award className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-neutral-900">{stats.low}</p>
                <p className="text-sm text-neutral-500">Low Threat</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Search competitors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-11"
            data-testid="search-competitors"
          />
        </div>
        <Select value={threatFilter} onValueChange={setThreatFilter}>
          <SelectTrigger className="w-[180px]" data-testid="threat-filter">
            <Filter className="w-4 h-4 mr-2 text-neutral-400" />
            <SelectValue placeholder="Threat Level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            <SelectItem value="high">High Threat</SelectItem>
            <SelectItem value="medium">Medium Threat</SelectItem>
            <SelectItem value="low">Low Threat</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Competitor Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCompetitors.map((competitor) => (
          <Card 
            key={competitor.id} 
            className="bg-white hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setExpandedCard(expandedCard === competitor.id ? null : competitor.id)}
            data-testid={`competitor-card-${competitor.id}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {competitor.name}
                    <Badge className={`text-xs ${THREAT_COLORS[competitor.threat_level]}`}>
                      {competitor.threat_level}
                    </Badge>
                  </CardTitle>
                  <CardDescription className="mt-1 line-clamp-2">
                    {competitor.description}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {/* Quick Info */}
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                {competitor.headquarters && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {competitor.headquarters}
                  </span>
                )}
                {competitor.website && (
                  <a 
                    href={competitor.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-purple-600 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Globe className="w-3 h-3" />
                    Website
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              {/* Services */}
              <div className="flex flex-wrap gap-1 mb-3">
                {competitor.services?.slice(0, 3).map((service, idx) => (
                  <Badge 
                    key={idx} 
                    variant="secondary" 
                    className={`text-xs ${SERVICE_COLORS[service] || 'bg-gray-100 text-gray-700'}`}
                  >
                    {service}
                  </Badge>
                ))}
                {competitor.services?.length > 3 && (
                  <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-700">
                    +{competitor.services.length - 3} more
                  </Badge>
                )}
              </div>

              {/* Markets */}
              <div className="text-xs text-gray-500 mb-3">
                <span className="font-medium">Markets: </span>
                {Array.isArray(competitor.markets) ? competitor.markets.join(", ") : competitor.markets}
              </div>

              {/* Expanded Content */}
              {expandedCard === competitor.id && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                  {/* SWOT Analysis */}
                  <div className="grid grid-cols-2 gap-3">
                    {competitor.strengths?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-green-700 mb-1">Strengths</p>
                        <ul className="text-xs text-gray-600 space-y-1">
                          {competitor.strengths.map((s, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <span className="text-green-500 mt-0.5">+</span> {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {competitor.weaknesses?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-red-700 mb-1">Weaknesses</p>
                        <ul className="text-xs text-gray-600 space-y-1">
                          {competitor.weaknesses.map((w, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <span className="text-red-500 mt-0.5">-</span> {w}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {competitor.opportunities?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-blue-700 mb-1">Opportunities</p>
                        <ul className="text-xs text-gray-600 space-y-1">
                          {competitor.opportunities.map((o, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <span className="text-blue-500 mt-0.5">★</span> {o}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {competitor.threats?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-orange-700 mb-1">Threats</p>
                        <ul className="text-xs text-gray-600 space-y-1">
                          {competitor.threats.map((t, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <span className="text-orange-500 mt-0.5">!</span> {t}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  {competitor.pricing && (
                    <div>
                      <p className="text-xs font-medium text-gray-700 mb-1">Pricing</p>
                      <p className="text-xs text-gray-600">{competitor.pricing}</p>
                    </div>
                  )}
                  {competitor.notes && (
                    <div>
                      <p className="text-xs font-medium text-gray-700 mb-1">Notes</p>
                      <p className="text-xs text-gray-600">{competitor.notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-gray-100">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); handleEdit(competitor); }}
                  data-testid={`edit-${competitor.id}`}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-red-500 hover:text-red-700"
                  onClick={(e) => { e.stopPropagation(); handleDelete(competitor.id); }}
                  data-testid={`delete-${competitor.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredCompetitors.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No competitors found</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => { resetForm(); setShowDialog(true); }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add your first competitor
          </Button>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCompetitor ? "Edit Competitor" : "Add Competitor"}</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2 sm:col-span-1">
              <Label>Company Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Take-e-way"
                data-testid="competitor-name-input"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label>Website</Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                placeholder="https://..."
              />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the competitor..."
                rows={2}
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label>Headquarters</Label>
              <Input
                value={formData.headquarters}
                onChange={(e) => setFormData({...formData, headquarters: e.target.value})}
                placeholder="e.g., Germany"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label>Threat Level</Label>
              <Select 
                value={formData.threat_level} 
                onValueChange={(v) => setFormData({...formData, threat_level: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label>Markets (comma-separated)</Label>
              <Input
                value={formData.markets}
                onChange={(e) => setFormData({...formData, markets: e.target.value})}
                placeholder="DE, FR, NL, ..."
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label>Services (comma-separated)</Label>
              <Input
                value={formData.services}
                onChange={(e) => setFormData({...formData, services: e.target.value})}
                placeholder="Packaging EPR, WEEE, ..."
              />
            </div>
            <div className="col-span-2">
              <Label>Pricing Info</Label>
              <Input
                value={formData.pricing}
                onChange={(e) => setFormData({...formData, pricing: e.target.value})}
                placeholder="e.g., Premium tier, €500/month..."
              />
            </div>
            
            {/* SWOT Analysis Section */}
            <div className="col-span-2">
              <p className="text-sm font-medium text-gray-700 mb-2 border-b pb-1">SWOT Analysis</p>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-green-700">Strengths (one per line)</Label>
              <Textarea
                value={formData.strengths}
                onChange={(e) => setFormData({...formData, strengths: e.target.value})}
                placeholder="Strong market presence&#10;Competitive pricing..."
                rows={3}
                className="border-green-200 focus:border-green-400"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-red-700">Weaknesses (one per line)</Label>
              <Textarea
                value={formData.weaknesses}
                onChange={(e) => setFormData({...formData, weaknesses: e.target.value})}
                placeholder="Limited coverage&#10;Outdated platform..."
                rows={3}
                className="border-red-200 focus:border-red-400"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-blue-700">Opportunities (one per line)</Label>
              <Textarea
                value={formData.opportunities}
                onChange={(e) => setFormData({...formData, opportunities: e.target.value})}
                placeholder="Expanding to new markets&#10;Partnership potential..."
                rows={3}
                className="border-blue-200 focus:border-blue-400"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-orange-700">Threats (one per line)</Label>
              <Textarea
                value={formData.threats}
                onChange={(e) => setFormData({...formData, threats: e.target.value})}
                placeholder="Price competition&#10;New market entrants..."
                rows={3}
                className="border-orange-200 focus:border-orange-400"
              />
            </div>
            
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-competitor-btn">
              {editingCompetitor ? "Update" : "Add"} Competitor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
